import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TableModule } from 'primeng/table';
import { UIChart } from 'primeng/chart';
import { forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { Project } from '../../models/project.model';
import { Employee } from '../../models/employee.model';
import { ProjectService } from '../../services/project.service';
import { EmployeeService } from '../../services/employee.service';
import { AuthService, UserInfo } from '../../services/auth.service';

interface PortfolioMetricCard {
  label: string;
  value: number;
  sublabel: string;
  icon: string;
  styleClass: string;
  filter: PortfolioFilter;
}

interface ProjectHealthRow {
  projectCode: string;
  projectName: string;
  clientName: string;
  managerName: string;
  serviceGroupName: string;
  progressLabel: string;
  progressPercent: number;
  statusLabel: string;
  statusClass: string;
  endDateLabel: string;
}

interface StatusBreakdownItem {
  label: string;
  value: number;
  percentage: number;
}

type HealthBucket = 'onTrack' | 'atRisk' | 'overdue';

type PortfolioFilter =
  | { type: 'all' }
  | { type: 'status'; value: HealthBucket }
  | { type: 'serviceGroup'; value: string }
  | { type: 'serviceGroupOthers' }
  | { type: 'manager'; value: string }
  | { type: 'managerOthers' }
  | { type: 'month'; value: 'current' };

@Component({
  selector: 'app-portfolio-overview',
  standalone: true,
  imports: [CommonModule, TableModule, UIChart],
  templateUrl: './portfolio-overview.component.html',
  styleUrl: './portfolio-overview.component.scss'
})
export class PortfolioOverviewComponent implements OnInit {
  cards: PortfolioMetricCard[] = [];
  allScopedProjects: Project[] = [];
  healthRows: ProjectHealthRow[] = [];
  statusChartData: any = { labels: [], datasets: [] };
  serviceGroupChartData: any = { labels: [], datasets: [] };
  managerChartData: any = { labels: [], datasets: [] };
  statusBreakdownItems: StatusBreakdownItem[] = [];
  serviceGroupChartKeys: string[] = [];
  serviceGroupTopKeys: string[] = [];
  managerChartKeys: string[] = [];
  managerTopKeys: string[] = [];
  statusChartOptions: any = {};
  loading = true;
  user: UserInfo | null = null;
  activeFilter: PortfolioFilter = { type: 'all' };

  constructor(
    private projectService: ProjectService,
    private employeeService: EmployeeService,
    private authService: AuthService
  ) {}

  ngOnInit(): void {
    this.user = this.authService.getUser();
    this.initializeChartOptions();
    this.loadMetrics();
  }

  private loadMetrics(): void {
    this.loading = true;

    forkJoin({
      projects: this.projectService.getAllProjects().pipe(catchError(() => of([] as Project[]))),
      employees: this.employeeService.getAllEmployees().pipe(catchError(() => of([] as Employee[])))
    }).subscribe({
      next: ({ projects, employees }) => {
        const scopedProjects = this.getScopedActiveProjects(projects, employees, this.user);
        this.allScopedProjects = scopedProjects;
        this.activeFilter = { type: 'all' };
        this.cards = this.buildCards(scopedProjects);
        this.buildCharts(scopedProjects);
        this.applyActiveFilter();
        this.loading = false;
      },
      error: () => {
        this.allScopedProjects = [];
        this.activeFilter = { type: 'all' };
        this.cards = this.buildCards([]);
        this.buildCharts([]);
        this.applyActiveFilter();
        this.loading = false;
      }
    });
  }

  private getScopedActiveProjects(projects: Project[], employees: Employee[], user: UserInfo | null): Project[] {
    let scoped = (projects || []).filter(p => p.isActive);

    if (user && !user.isAdmin && user.isPM) {
      const currentEmployee = employees.find(
        e => e.email?.toLowerCase() === user.email.toLowerCase()
      );
      scoped = currentEmployee ? scoped.filter(p => p.managerId === currentEmployee.id) : [];
    }

    return scoped;
  }

  private buildCards(projects: Project[]): PortfolioMetricCard[] {
    const totalProjects = projects.length;
    const uniqueClientCount = new Set(
      projects.map(p => (p.clientId || p.clientName || '').trim()).filter(Boolean)
    ).size;
    const statusBuckets = projects.reduce(
      (acc, project) => {
        const health = this.getHealthBucket(project);
        acc[health] += 1;
        return acc;
      },
      { onTrack: 0, atRisk: 0, overdue: 0 }
    );
    const onTrackCount = statusBuckets.onTrack;
    const atRiskCount = statusBuckets.atRisk;
    const overdueCount = statusBuckets.overdue;
    const completingThisMonthCount = projects.filter(p => this.isCompletingThisMonth(p)).length;
    const onTrackPercentage = totalProjects > 0 ? Math.round((onTrackCount / totalProjects) * 100) : 0;

    return [
      {
        label: 'Total projects',
        value: totalProjects,
        sublabel: `Across ${uniqueClientCount} clients`,
        icon: 'pi pi-briefcase',
        styleClass: 'stats-card--total',
        filter: { type: 'all' }
      },
      {
        label: 'On track',
        value: onTrackCount,
        sublabel: `${onTrackPercentage}% of portfolio`,
        icon: 'pi pi-check-circle',
        styleClass: 'stats-card--success',
        filter: { type: 'status', value: 'onTrack' }
      },
      {
        label: 'At risk',
        value: atRiskCount,
        sublabel: 'Review required',
        icon: 'pi pi-exclamation-triangle',
        styleClass: 'stats-card--warning',
        filter: { type: 'status', value: 'atRisk' }
      },
      {
        label: 'Overdue',
        value: overdueCount,
        sublabel: 'Immediate action',
        icon: 'pi pi-times-circle',
        styleClass: 'stats-card--danger',
        filter: { type: 'status', value: 'overdue' }
      },
      {
        label: 'Completing this month',
        value: completingThisMonthCount,
        sublabel: this.getCurrentMonthSublabel(),
        icon: 'pi pi-calendar',
        styleClass: 'stats-card--total',
        filter: { type: 'month', value: 'current' }
      }
    ];
  }

  private buildHealthRows(projects: Project[]): ProjectHealthRow[] {
    return [...projects]
      .sort((a, b) => this.compareEndDate(a.endDate, b.endDate))
      .map(project => {
        const health = this.getHealthBucket(project);
        return {
          projectCode: project.projectCode?.trim() || this.getFallbackCode(project.id),
          projectName: project.name || 'Untitled Project',
          clientName: project.clientName?.trim() || 'N/A',
          managerName: project.managerName?.trim() || 'Unassigned',
          serviceGroupName: project.serviceGroupName?.trim() || 'Unassigned',
          progressLabel: this.getProgressLabel(project, health),
          progressPercent: this.getProgressPercent(project, health),
          statusLabel: this.getStatusLabel(health),
          statusClass: this.getStatusClass(health),
          endDateLabel: this.formatEndDate(project.endDate)
        };
      });
  }

  private buildCharts(projects: Project[]): void {
    const statusCounts = { onTrack: 0, atRisk: 0, overdue: 0 };
    projects.forEach(project => {
      const health = this.getHealthBucket(project);
      statusCounts[health] += 1;
    });
    this.statusBreakdownItems = this.buildStatusBreakdownItems(statusCounts);

    this.statusChartData = {
      labels: this.statusBreakdownItems.map(
        item => `${item.label}: ${item.value} (${item.percentage}%)`
      ),
      datasets: [
        {
          data: [statusCounts.onTrack, statusCounts.atRisk, statusCounts.overdue],
          backgroundColor: ['#22c55e', '#f59e0b', '#ef4444'],
          hoverBackgroundColor: ['#16a34a', '#d97706', '#dc2626'],
          borderWidth: 0,
          cutout: '70%'
        }
      ]
    };

    const serviceGroupCounts = this.buildCountMap(
      projects,
      project => project.serviceGroupName?.trim() || 'Unassigned'
    );
    const managerCounts = this.buildCountMap(
      projects,
      project => project.managerName?.trim() || 'Unassigned'
    );

    const serviceGroupChart = this.toDoughnutChartData(
      serviceGroupCounts,
      ['#2563eb', '#10b981', '#6366f1', '#d97706', '#ec4899', '#0ea5e9', '#94a3b8'],
      6,
      true,
      'valuePercentage'
    );
    this.serviceGroupChartData = serviceGroupChart.data;
    this.serviceGroupChartKeys = serviceGroupChart.keys;
    this.serviceGroupTopKeys = serviceGroupChart.topKeys;

    const managerChart = this.toDoughnutChartData(
      managerCounts,
      ['#1d4ed8', '#2563eb', '#3b82f6', '#60a5fa', '#93c5fd', '#bfdbfe', '#dbeafe'],
      6,
      true,
      'valueOnly'
    );
    this.managerChartData = managerChart.data;
    this.managerChartKeys = managerChart.keys;
    this.managerTopKeys = managerChart.topKeys;
  }

  private buildCountMap(
    projects: Project[],
    keySelector: (project: Project) => string
  ): Map<string, number> {
    const counts = new Map<string, number>();
    for (const project of projects) {
      const key = keySelector(project);
      counts.set(key, (counts.get(key) || 0) + 1);
    }
    return counts;
  }

  private toDoughnutChartData(
    counts: Map<string, number>,
    colors: string[],
    topN: number,
    includeOthers: boolean,
    labelMode: 'labelOnly' | 'valueOnly' | 'valuePercentage'
  ): { data: any; keys: string[]; topKeys: string[] } {
    const sorted = [...counts.entries()].sort((a, b) => b[1] - a[1]);
    const top = sorted.slice(0, topN);
    const topKeys = top.map(([label]) => label);
    if (includeOthers) {
      const othersCount = sorted.slice(topN).reduce((sum, entry) => sum + entry[1], 0);
      if (othersCount > 0) {
        top.push(['Others', othersCount]);
      }
    }
    const total = top.reduce((sum, [, value]) => sum + value, 0);
    const labels = top.map(([label, value]) => {
      if (labelMode === 'valueOnly') {
        return `${label}: ${value}`;
      }
      if (labelMode === 'valuePercentage') {
        const percentage = total > 0 ? Math.round((value / total) * 100) : 0;
        return `${label}: ${value} (${percentage}%)`;
      }
      return label;
    });

    return {
      topKeys,
      keys: top.map(([label]) => label),
      data: {
        labels,
        datasets: [
          {
            data: top.map(([, value]) => value),
            backgroundColor: top.map((_, index) => colors[index % colors.length]),
            borderWidth: 0,
            cutout: '70%'
          }
        ]
      }
    };
  }

  private buildStatusBreakdownItems(statusCounts: { onTrack: number; atRisk: number; overdue: number }): StatusBreakdownItem[] {
    const entries = [
      { label: 'On track', value: statusCounts.onTrack },
      { label: 'At risk', value: statusCounts.atRisk },
      { label: 'Overdue', value: statusCounts.overdue }
    ];
    const total = entries.reduce((sum, entry) => sum + entry.value, 0);
    return entries.map(entry => ({
      label: entry.label,
      value: entry.value,
      percentage: total > 0 ? Math.round((entry.value / total) * 100) : 0
    }));
  }

  private initializeChartOptions(): void {
    this.statusChartOptions = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: 'right',
          labels: {
            usePointStyle: true,
            pointStyle: 'circle',
            boxWidth: 10
          }
        }
      }
    };

  }

  onKpiCardClick(card: PortfolioMetricCard): void {
    const nextFilter = card.filter;
    this.activeFilter = this.isSameFilter(this.activeFilter, nextFilter) ? { type: 'all' } : nextFilter;
    this.applyActiveFilter();
  }

  onStatusChartSelect(event: any): void {
    const index = this.getSelectedIndex(event);
    if (index === null) {
      return;
    }
    const labels: HealthBucket[] = ['onTrack', 'atRisk', 'overdue'];
    const selected = labels[index];
    if (!selected) {
      return;
    }
    this.toggleFilter({ type: 'status', value: selected });
  }

  onServiceGroupChartSelect(event: any): void {
    const index = this.getSelectedIndex(event);
    if (index === null) {
      return;
    }
    const selected = this.serviceGroupChartKeys[index];
    if (!selected) {
      return;
    }
    if (selected === 'Others') {
      this.toggleFilter({ type: 'serviceGroupOthers' });
      return;
    }
    this.toggleFilter({ type: 'serviceGroup', value: selected });
  }

  onManagerChartSelect(event: any): void {
    const index = this.getSelectedIndex(event);
    if (index === null) {
      return;
    }
    const selected = this.managerChartKeys[index];
    if (!selected) {
      return;
    }
    if (selected === 'Others') {
      this.toggleFilter({ type: 'managerOthers' });
      return;
    }
    this.toggleFilter({ type: 'manager', value: selected });
  }

  clearFilter(): void {
    this.activeFilter = { type: 'all' };
    this.applyActiveFilter();
  }

  isCardSelected(card: PortfolioMetricCard): boolean {
    return this.isSameFilter(this.activeFilter, card.filter);
  }

  hasActiveFilter(): boolean {
    return this.activeFilter.type !== 'all';
  }

  getActiveFilterLabel(): string {
    if (this.activeFilter.type === 'all') {
      return 'All active projects';
    }
    if (this.activeFilter.type === 'status') {
      if (this.activeFilter.value === 'onTrack') {
        return 'Status: On track';
      }
      if (this.activeFilter.value === 'atRisk') {
        return 'Status: At risk';
      }
      return 'Status: Overdue';
    }
    if (this.activeFilter.type === 'serviceGroup') {
      return `Service Group: ${this.activeFilter.value}`;
    }
    if (this.activeFilter.type === 'serviceGroupOthers') {
      return 'Service Group: Others';
    }
    if (this.activeFilter.type === 'manager') {
      return `Manager: ${this.activeFilter.value}`;
    }
    if (this.activeFilter.type === 'managerOthers') {
      return 'Manager: Others';
    }
    return 'Completing this month';
  }

  private toggleFilter(nextFilter: PortfolioFilter): void {
    this.activeFilter = this.isSameFilter(this.activeFilter, nextFilter) ? { type: 'all' } : nextFilter;
    this.applyActiveFilter();
  }

  private applyActiveFilter(): void {
    let filteredProjects = this.allScopedProjects;
    const filter = this.activeFilter;
    switch (filter.type) {
      case 'status':
        filteredProjects = filteredProjects.filter(project => this.getHealthBucket(project) === filter.value);
        break;
      case 'serviceGroup':
        filteredProjects = filteredProjects.filter(
          project => (project.serviceGroupName?.trim() || 'Unassigned') === filter.value
        );
        break;
      case 'manager':
        filteredProjects = filteredProjects.filter(
          project => (project.managerName?.trim() || 'Unassigned') === filter.value
        );
        break;
      case 'serviceGroupOthers':
        filteredProjects = filteredProjects.filter(
          project => !this.serviceGroupTopKeys.includes(project.serviceGroupName?.trim() || 'Unassigned')
        );
        break;
      case 'managerOthers':
        filteredProjects = filteredProjects.filter(
          project => !this.managerTopKeys.includes(project.managerName?.trim() || 'Unassigned')
        );
        break;
      case 'month':
        filteredProjects = filteredProjects.filter(project => this.isCompletingThisMonth(project));
        break;
      case 'all':
      default:
        break;
    }
    this.healthRows = this.buildHealthRows(filteredProjects);
  }

  private isSameFilter(left: PortfolioFilter, right: PortfolioFilter): boolean {
    if (left.type !== right.type) {
      return false;
    }
    if (left.type === 'all' && right.type === 'all') {
      return true;
    }
    if (left.type === 'status' && right.type === 'status') {
      return left.value === right.value;
    }
    if (left.type === 'serviceGroup' && right.type === 'serviceGroup') {
      return left.value === right.value;
    }
    if (left.type === 'serviceGroupOthers' && right.type === 'serviceGroupOthers') {
      return true;
    }
    if (left.type === 'manager' && right.type === 'manager') {
      return left.value === right.value;
    }
    if (left.type === 'managerOthers' && right.type === 'managerOthers') {
      return true;
    }
    if (left.type === 'month' && right.type === 'month') {
      return left.value === right.value;
    }
    return false;
  }

  private getSelectedIndex(event: any): number | null {
    const index =
      event?.element?.index ??
      event?.element?._index ??
      event?.element?.$context?.dataIndex ??
      event?.element?.$context?.index ??
      event?.dataIndex;
    return typeof index === 'number' ? index : null;
  }

  private getHealthBucket(project: Project): HealthBucket {
    if (this.isOverdue(project)) {
      return 'overdue';
    }
    if (this.isAtRisk(project)) {
      return 'atRisk';
    }
    return 'onTrack';
  }

  private getProgressLabel(project: Project, health: HealthBucket): string {
    const progress = (project.progress || '').trim();
    if (progress) {
      return progress;
    }
    if (health === 'onTrack') {
      return 'On Track';
    }
    if (health === 'atRisk') {
      return 'At Risk';
    }
    return 'Overdue';
  }

  private getProgressPercent(project: Project, health: HealthBucket): number {
    const progress = (project.progress || '').trim().toLowerCase();
    const percentageMatch = progress.match(/(\d{1,3})\s*%/);
    if (percentageMatch) {
      const parsed = Number(percentageMatch[1]);
      return Math.max(0, Math.min(100, parsed));
    }
    if (this.isClosed(project)) {
      return 100;
    }
    if (health === 'onTrack') {
      return 78;
    }
    if (health === 'atRisk') {
      return 46;
    }
    return 22;
  }

  private getStatusLabel(health: HealthBucket): string {
    if (health === 'onTrack') {
      return 'On track';
    }
    if (health === 'atRisk') {
      return 'At risk';
    }
    return 'Overdue';
  }

  private getStatusClass(health: HealthBucket): string {
    if (health === 'onTrack') {
      return 'status-pill--success';
    }
    if (health === 'atRisk') {
      return 'status-pill--warning';
    }
    return 'status-pill--danger';
  }

  private formatEndDate(value?: string): string {
    const parsed = value ? this.parseDate(value) : null;
    if (!parsed) {
      return 'N/A';
    }
    return parsed.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: '2-digit'
    });
  }

  private compareEndDate(a?: string, b?: string): number {
    const aDate = a ? this.parseDate(a) : null;
    const bDate = b ? this.parseDate(b) : null;
    if (!aDate && !bDate) {
      return 0;
    }
    if (!aDate) {
      return 1;
    }
    if (!bDate) {
      return -1;
    }
    return aDate.getTime() - bDate.getTime();
  }

  private getFallbackCode(id?: string): string {
    if (!id) {
      return 'N/A';
    }
    return id.slice(0, 8).toUpperCase();
  }

  private isAtRisk(project: Project): boolean {
    const progress = (project.progress || '').trim().toLowerCase();
    const status = (project.status || '').trim().toLowerCase();
    if (progress !== 'off track') {
      return false;
    }
    return status === 'red' || status === 'amber' || status === 'yellow';
  }

  private isOverdue(project: Project): boolean {
    if (!project.endDate || this.isClosed(project)) {
      return false;
    }
    const end = this.parseDate(project.endDate);
    if (!end) {
      return false;
    }
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return end < today;
  }

  private isCompletingThisMonth(project: Project): boolean {
    if (!project.endDate) {
      return false;
    }
    const end = this.parseDate(project.endDate);
    if (!end) {
      return false;
    }
    const now = new Date();
    return end.getFullYear() === now.getFullYear() && end.getMonth() === now.getMonth();
  }

  private isClosed(project: Project): boolean {
    const progress = (project.progress || '').trim().toLowerCase();
    return progress === 'closed' || progress === 'completed' || progress === 'complete';
  }

  private parseDate(value: string): Date | null {
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) {
      return null;
    }
    parsed.setHours(0, 0, 0, 0);
    return parsed;
  }

  private getCurrentMonthSublabel(): string {
    const month = new Date().toLocaleString('en-US', { month: 'short' });
    const year = new Date().getFullYear();
    return `End date in ${month} ${year}`;
  }
}
